import PortalIdParser from 'PortalIdParser';
import { getFullUrl } from 'hubspot-url-utils';
import { ALL_AVAILABLE_ADD_ONS } from 'pricing-pages-lib/constants/AllAvailableAddOns';
import { ALL_SERVICES } from 'pricing-pages-lib/constants/AllAvailableServices';
import { getOfferIdForSkuInProductConfigurations, getProductBasedOfferId } from 'pricing-pages-lib/utils/offerUtils';
import { getHasProductInProductInventory, getHasProductVersionInProductInventory, getProductsFromProductInventoryQuantity, getProductVersionsFromProductInventory } from 'pricing-pages-lib/utils/productInventoryUtils';
import { getCoreSeatSkuForProductLevel, getCrmSuiteProductLevelForProductConfigurations, getCrmSuiteProductLevelForSkuIds, getCurrentProductVersion, getCurrentSkuIdForProduct, getHighestProductLevelFromSkuIds, getIncludedCoreSeatsQuantityForProductVersion, getProductForSkuId, getQuantityPackForProductVersion, getSkuIdIsAddonOrService, getSkuIdIsHub, getSkuIdIsInCrmSuite, getSkuIdIsInTBSuiteForProductLevel, getTBSuiteProductForProductLevel, getTBSuiteProductLevelForSkuIds } from 'pricing-pages-lib/utils/productsUtils';
import { getIsStarterCoreSeatRequired, getIsStarterCoreSeatRequiredForSkuIds, getRequiredOnboardingSkuId } from 'pricing-pages-lib/utils/requiredOnboardingUtils';
import { CORE_SEATS_INCLUDED_STARTER } from 'self-service-api/constants/ApiNames';
import { NON_RECURRING, RECURRING } from 'self-service-api/constants/ChargeTypes';
import { currentOrderedPaidProductLevels, STARTER } from 'self-service-api/constants/ProductLevels';
import { CMS, MARKETING, OPERATIONS, SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { PRODUCT_VERSION, QUANTITY_PACK } from 'self-service-api/constants/SkuTypes';
import { isMarketingContactsSku } from 'self-service-api/core/utilities/isMarketingContactsSku';
import { getIsUngatedForSeats } from './gatingUtils';
import { CRM_SUITE_PRODUCTS } from './productsUtils';
import { stringifyQueryParams } from './queryParamUtils';
export const createProductConfig = ({
  skuId,
  quantity = 1
}) => ({
  skuId,
  quantity
});
export const getAdditionalContactQuantityFromConfigurations = ({
  productConfigurations,
  quantityPackSkuId
}) => {
  const contactsConfiguration = productConfigurations.find(configuration => configuration.skuId === quantityPackSkuId);
  if (!contactsConfiguration) return 0;
  return contactsConfiguration.quantity;
}; // The purpose of this helper is to override the additional contacts quantity for
// sending to checkout (ex. if the user slides the slider to have more contacts,
// we would want to update the product configurations we will send to offer-builder)

export const getUpdatedContactsQuantityConfig = ({
  productConfigurations,
  quantityPackSkuId,
  quantityPackQuantity
}) => {
  const clone = productConfigurations.map(configuration => Object.assign({}, configuration));
  let existingContactConfigIndex = -1;

  for (let i = 0; i < clone.length; i++) {
    if (clone[i].skuId === quantityPackSkuId) existingContactConfigIndex = i;
  }

  if (existingContactConfigIndex > -1) {
    if (quantityPackQuantity === 0) {
      clone.splice(existingContactConfigIndex, 1);
    } else {
      clone[existingContactConfigIndex].quantity = quantityPackQuantity;
    }

    return clone;
  }

  if (quantityPackQuantity === 0) {
    return clone;
  }

  return [...clone, {
    skuId: quantityPackSkuId,
    quantity: quantityPackQuantity
  }];
};
export const getCheckoutProductConfigurationsFromSkuId = (state, {
  productConfiguration,
  subtractOwnedUnitsIfBuyingNoAdditional = false,
  subtractOwnedSuiteProducts = false,
  suiteProductLevel,
  allProductConfigurations
}) => {
  const {
    skuId,
    quantity
  } = productConfiguration;
  const returnConfigurations = [];
  let adjustedQuantity = quantity;
  const {
    productData: {
      skuMap
    }
  } = state;
  const sku = skuMap[skuId];
  const skuIdIsHub = getSkuIdIsHub(state, {
    skuId
  });
  const skuIdIsAddonOrService = getSkuIdIsAddonOrService(state, {
    skuId
  });
  const isQuantifiableHub = skuIdIsHub && sku.quantifiable;

  const getAdjustedConfiguration = (skuIdToAdjust, currentAdjustedQuantity) => {
    const ownedQuantity = getProductsFromProductInventoryQuantity(state, {
      skuId: skuIdToAdjust
    });

    if (ownedQuantity === currentAdjustedQuantity) {
      returnConfigurations.push(createProductConfig({
        skuId: skuIdToAdjust,
        quantity: 0
      }));
      return returnConfigurations;
    }

    return null;
  };

  if (sku.skuType === PRODUCT_VERSION && skuIdIsHub) {
    const ownsProductVersion = getHasProductVersionInProductInventory(state, {
      skuId
    });

    if (suiteProductLevel && ownsProductVersion && subtractOwnedSuiteProducts) {
      adjustedQuantity = 0;
    }

    const canIncludedOwnedProductForSuite = suiteProductLevel && getSkuIdIsInCrmSuite(state, {
      skuId,
      suiteProductLevel
    });

    if (!ownsProductVersion || canIncludedOwnedProductForSuite) {
      const requiredOnboardingSkuId = getRequiredOnboardingSkuId(state, {
        skuId
      });

      if (requiredOnboardingSkuId) {
        const productBasedOfferId = getProductBasedOfferId(state, {
          skuId: requiredOnboardingSkuId
        }); // Skus with productBasedOffers have to be included in a different
        // field when hitting offer-builder, so we don't include them here

        if (!productBasedOfferId) {
          returnConfigurations.push(createProductConfig({
            skuId: requiredOnboardingSkuId
          }));
        }
      }
    } else if (skuIdIsHub) {
      if (isQuantifiableHub) {
        const adjustedConfiguration = getAdjustedConfiguration(skuId, adjustedQuantity);
        if (adjustedConfiguration) return adjustedConfiguration;
      } else {
        adjustedQuantity = 0;
      }
    }
  } else if (sku.skuType === QUANTITY_PACK) {
    const {
      productLine
    } = getProductForSkuId(state, {
      skuId
    });

    if (productLine === MARKETING) {
      const isMarketingContactSku = isMarketingContactsSku({
        skuMap,
        skuId
      });

      if (!isMarketingContactSku) {
        const {
          quantityUnit: {
            includedWithProductVersion
          }
        } = sku;
        adjustedQuantity += includedWithProductVersion;
      }
    }

    const {
      productVersionSkuId
    } = sku;
    const configsContainProductVersion = allProductConfigurations.some(({
      skuId: searchSkuId
    }) => searchSkuId === productVersionSkuId);

    if (subtractOwnedUnitsIfBuyingNoAdditional && !configsContainProductVersion) {
      const adjustedConfiguration = getAdjustedConfiguration(skuId, adjustedQuantity);
      if (adjustedConfiguration) return adjustedConfiguration;
    }
  } else if (skuIdIsAddonOrService) {
    const adjustedConfiguration = getAdjustedConfiguration(skuId, adjustedQuantity);
    if (adjustedConfiguration) return adjustedConfiguration;
  }

  const someConfigHasQuantity = allProductConfigurations.some(({
    quantity: tempQuantity
  }) => tempQuantity);

  if (adjustedQuantity > 0 || !someConfigHasQuantity) {
    returnConfigurations.push(createProductConfig({
      skuId,
      quantity: adjustedQuantity
    }));
  }

  return returnConfigurations;
}; // Filters out itemConfigs that have duplicated merchandiseIds.
// Intended to filter out duplicated HSFS for Startups as seen on this
// issue: https://zenhub.hubteam.com/app/workspaces/growth-cbx-5c2d3d676b243f914638ef39/issues/hubspot/pricing-pages-unified-ui/3251

export const getUniqueProductConfigurations = productConfigurations => productConfigurations.filter((config, index) => {
  return productConfigurations.map(c => c.skuId).indexOf(config.skuId) === index;
});
export const getTBSuiteCheckoutProductConfigurations = (state, {
  productConfigurations: originalProductConfigurations,
  subtractOwnedUnitsIfBuyingNoAdditional = false,
  subtractOwnedSuiteProducts = false
}) => {
  const suiteProductLevel = getTBSuiteProductLevelForSkuIds(state, {
    skuIds: originalProductConfigurations.map(({
      skuId
    }) => skuId)
  });
  let productConfigurations = [...originalProductConfigurations];

  if (suiteProductLevel) {
    const tbProduct = getTBSuiteProductForProductLevel(state, {
      suiteProductLevel
    });
    productConfigurations = productConfigurations.filter(productConfiguration => !getSkuIdIsInTBSuiteForProductLevel(state, {
      skuId: productConfiguration.skuId,
      suiteProductLevel
    }));
    productConfigurations.push(createProductConfig({
      skuId: tbProduct.currentSkuId,
      quantity: 1
    }));
  }

  let productConfigs = productConfigurations.reduce((acc, productConfiguration) => acc.concat(getCheckoutProductConfigurationsFromSkuId(state, {
    productConfiguration,
    subtractOwnedUnitsIfBuyingNoAdditional,
    subtractOwnedSuiteProducts,
    suiteProductLevel,
    allProductConfigurations: productConfigurations
  })), []); // The zero state for marketing calc when you own the base plan is a contacts config with quantity 0.
  // This is done so we still get the "Buy additional contacts" CTA even though nothing is really selected
  // But if we add an add-on, we want to remove the empty contacts config so offer id resolution knows
  // we are really just buying an addon

  const someCheckoutConfigHasQuantity = productConfigs.some(({
    quantity
  }) => quantity);
  productConfigs = productConfigs.filter(({
    quantity
  }) => quantity > 0 || !someCheckoutConfigHasQuantity); // If a one-time fee is a mandatory merch for more than one SKU of bundleSelections.
  // It will be added multiple times to itemConfigs.
  // The following operation assures such OTF appears only once in itemConfigs.

  return getUniqueProductConfigurations(productConfigs);
};
export const getCheckoutProductConfigurations = (state, {
  productConfigurations,
  subtractOwnedUnitsIfBuyingNoAdditional = false,
  subtractOwnedSuiteProducts = false
}) => {
  const suiteProductLevel = getCrmSuiteProductLevelForSkuIds(state, {
    skuIds: productConfigurations.map(({
      skuId
    }) => skuId)
  });
  let productConfigs = productConfigurations.reduce((acc, productConfiguration) => acc.concat(getCheckoutProductConfigurationsFromSkuId(state, {
    productConfiguration,
    subtractOwnedUnitsIfBuyingNoAdditional,
    subtractOwnedSuiteProducts,
    suiteProductLevel,
    allProductConfigurations: productConfigurations
  })), []); // The zero state for marketing calc when you own the base plan is a contacts config with quantity 0.
  // This is done so we still get the "Buy additional contacts" CTA even though nothing is really selected
  // But if we add an add-on, we want to remove the empty contacts config so offer id resolution knows
  // we are really just buying an addon

  const someCheckoutConfigHasQuantity = productConfigs.some(({
    quantity
  }) => quantity);
  productConfigs = productConfigs.filter(({
    quantity
  }) => quantity > 0 || !someCheckoutConfigHasQuantity); // If a one-time fee is a mandatory merch for more than one SKU of bundleSelections.
  // It will be added multiple times to itemConfigs.
  // The following operation assures such OTF appears only once in itemConfigs.

  return getUniqueProductConfigurations(productConfigs);
};

const getFilteredProductConfigurationsByChargeType = (state, {
  productConfigurations,
  chargeType
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  return productConfigurations.filter(({
    skuId
  }) => {
    const productInfo = getProductForSkuId(state, {
      skuId
    });
    return productInfo.chargeType === chargeType;
  }).map(({
    skuId,
    quantity
  }) => {
    const skuInfo = skuMap[skuId];

    if (skuInfo.skuType === QUANTITY_PACK) {
      const {
        includedWithQuantityPack
      } = skuInfo.quantityUnit;
      const calculatedQuantity = Math.ceil(quantity / includedWithQuantityPack);
      return {
        skuId,
        quantity: calculatedQuantity
      };
    }

    return {
      skuId,
      quantity
    };
  });
};
/**
 * Takes in a list of product configurations and filters it down to a list of non-recurring
 * product configurations configurations.
 */


export const getNonRecurringProductConfigurations = (state, {
  productConfigurations
}) => getFilteredProductConfigurationsByChargeType(state, {
  productConfigurations,
  chargeType: NON_RECURRING
});
/**
 * Takes in a list of product configurations and query param term and splits them up into a
 * nested array of recurring product configurations that share the same offer.
 */

export const getOfferProductConfigurations = (state, {
  productConfigurations,
  queryParamTerm
}) => {
  const offerProductConfigurations = [];
  const recurringProductConfigurations = getFilteredProductConfigurationsByChargeType(state, {
    productConfigurations,
    chargeType: RECURRING
  });
  recurringProductConfigurations.forEach(productConfiguration => {
    const offerIdForSkuId = getOfferIdForSkuInProductConfigurations(state, {
      allProductConfigurations: recurringProductConfigurations,
      productConfiguration,
      queryParamTerm
    });
    const {
      productLine
    } = getProductForSkuId(state, {
      skuId: productConfiguration.skuId
    });

    if (offerIdForSkuId) {
      offerProductConfigurations.push({
        offerId: typeof offerIdForSkuId === 'number' ? offerIdForSkuId : offerIdForSkuId[productLine],
        productConfigurations: [productConfiguration]
      });
    }
  });
  const groupedOfferProductConfigurations = offerProductConfigurations.reduce((acc, offerProductConfiguration) => {
    const {
      offerId,
      productConfigurations: currentProductConfigurations
    } = offerProductConfiguration;
    const {
      productConfigurations: allProductConfigurationsForOfferId
    } = acc.find(({
      offerId: offerIdAdded
    }) => offerIdAdded === offerId) || {
      productConfigurations: []
    };
    const filteredAcc = acc.filter(({
      offerId: offerIdAdded
    }) => offerIdAdded !== offerId);
    return [...filteredAcc, {
      offerId,
      productConfigurations: [...allProductConfigurationsForOfferId, ...currentProductConfigurations]
    }];
  }, []);
  return groupedOfferProductConfigurations;
};
export const getProductLevelInConfigs = (state, {
  productConfigurations,
  productLine
}) => {
  const foundConfig = productConfigurations.find(({
    skuId
  }) => {
    const {
      productLine: searchProductLine,
      productLevel
    } = getProductForSkuId(state, {
      skuId
    });
    return productLevel && searchProductLine === productLine;
  });

  if (foundConfig) {
    return getProductForSkuId(state, {
      skuId: foundConfig.skuId
    }).productLevel;
  }

  return null;
};
/**
 * Sorts a list of product configurations to match the order of products
 * shown in the calculator. At time of writing it's sorted by: Marketing,
 * Sales, Service, CMS, Operations, Add-Ons, Services
 */

export const getSortedProductConfigurations = (state, {
  productConfigurations
}) => {
  const skuMap = state.productData.skuMap;
  const sorted = [...productConfigurations];
  sorted.sort((configA, configB) => {
    const productA = getProductForSkuId(state, {
      skuId: configA.skuId
    });
    const skuA = skuMap[configA.skuId];
    const productB = getProductForSkuId(state, {
      skuId: configB.skuId
    });
    const skuB = skuMap[configB.skuId];

    if (productA.productLine && productA.productLevel) {
      if (!productB.productLevel || !productB.productLine) {
        // Product A is a hub or quantity pack, product B is an addon / service
        return -1;
      }

      if (productA.productLine === productB.productLine) {
        // Multiple skus in the same product line, sort quantity packs later
        if (skuA.skuType === PRODUCT_VERSION) {
          if (skuB.skuType === QUANTITY_PACK) {
            return -1;
          }
        } else if (skuA.skuType === QUANTITY_PACK) {
          if (skuB.skuType === PRODUCT_VERSION) {
            return 1;
          }
        }
      } else {
        // Different product lines, sort accordingly
        const orderedProductLines = [MARKETING, SALES, SERVICE, CMS, OPERATIONS];
        return orderedProductLines.indexOf(productA.productLine) - orderedProductLines.indexOf(productB.productLine);
      }
    } else if (productB.productLine && productB.productLevel) {
      // Product B is a hub or quantity pack, product A is an addon / service
      return 1;
    } else {
      // Both are Addons / Services
      const orderedAddonsAndServices = [...ALL_AVAILABLE_ADD_ONS, ...ALL_SERVICES];

      if (orderedAddonsAndServices.includes(productA.apiName)) {
        if (orderedAddonsAndServices.includes(productB.apiName)) {
          return orderedAddonsAndServices.indexOf(productA.apiName) - orderedAddonsAndServices.indexOf(productB.apiName);
        } else {
          return 1;
        }
      } else if (orderedAddonsAndServices.includes(productB.apiName)) {
        return -1;
      }
    }

    return 0;
  });
  return sorted;
};
export const getSuiteProductConfigurations = (state, {
  productLevel
}) => {
  const crmSuiteProductLines = CRM_SUITE_PRODUCTS[productLevel];
  const productConfigs = crmSuiteProductLines.map(productLine => {
    const skuId = getCurrentSkuIdForProduct(state, {
      productLine,
      productLevel
    });
    return skuId && createProductConfig({
      skuId
    });
  });
  return productConfigs.filter(config => !!config);
};
/**
 * Looks at a list of configurations as well as what you own, and sees if you are
 * making an upgrade to CRM suite. If so, this adds the hubs you own already to
 * the returned list so they can be sent to checkout again to restart your subscriptions
 * under the suite.
 * @param state
 * @param productConfigurations
 */

export const getConfigsWithSuiteIfPossible = (state, {
  productConfigurations
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const configsAlreadyContainSuite = getCrmSuiteProductLevelForProductConfigurations(state, {
    productConfigurations
  });
  if (configsAlreadyContainSuite) return productConfigurations;
  const suiteProductLevel = getCrmSuiteProductLevelForSkuIds(state, {
    skuIds: [...productConfigurations.map(({
      skuId
    }) => skuId), ...getProductVersionsFromProductInventory(state).map(({
      skuId
    }) => skuId)]
  });

  if (!suiteProductLevel) {
    return productConfigurations;
  }

  const newProductConfigurations = [...productConfigurations];
  const productLinesNeededToAdd = CRM_SUITE_PRODUCTS[suiteProductLevel].filter(productLine => !productConfigurations.some(({
    skuId
  }) => {
    const {
      skuType
    } = skuMap[skuId];
    const {
      productLine: searchProductLine
    } = getProductForSkuId(state, {
      skuId
    });
    return productLine === searchProductLine && skuType === PRODUCT_VERSION;
  }));
  productLinesNeededToAdd.forEach(productLine => {
    const skuId = getCurrentSkuIdForProduct(state, {
      productLine,
      productLevel: suiteProductLevel
    });

    if (skuId) {
      newProductConfigurations.push(createProductConfig({
        skuId
      }));
      const {
        quantityPackDetails
      } = skuMap[skuId];
      quantityPackDetails.forEach(({
        skuId: quantityPackSkuId
      }) => {
        if (!newProductConfigurations.some(({
          skuId: searchSkuId
        }) => searchSkuId === quantityPackSkuId)) {
          const ownedQuantity = getProductsFromProductInventoryQuantity(state, {
            skuId: quantityPackSkuId
          });
          if (ownedQuantity > 0) newProductConfigurations.push(createProductConfig({
            skuId: quantityPackSkuId,
            quantity: ownedQuantity
          }));
        }
      });
    }
  });
  return getSortedProductConfigurations(state, {
    productConfigurations: newProductConfigurations
  });
};
/**
 * Adds onboarding product configs that have product-based offers associated with them.
 * This is useful for getting a full list of the checkout merch since PBOs don't
 * get included in the same productConfigurations list we send to checkout
 */

export const getConfigurationsWithPBORequiredOnboarding = (state, {
  productConfigurations
}) => {
  const extraSkuIds = {};
  productConfigurations.forEach(({
    skuId
  }) => {
    const onboardingSkuId = getRequiredOnboardingSkuId(state, {
      skuId
    });

    if (onboardingSkuId) {
      const productBasedOffer = getProductBasedOfferId(state, {
        skuId: onboardingSkuId
      });
      if (productBasedOffer) extraSkuIds[onboardingSkuId] = true;
    }
  });
  return [...productConfigurations, ...Object.keys(extraSkuIds).map(skuIdStr => createProductConfig({
    skuId: parseInt(skuIdStr, 10)
  }))];
};
/**
 * A helper to get product configurations for a ProductVersion, optionally adding
 * additional units for the first quantity pack on the ProductVersion. If the ProductVersion
 * is owned, returns a config for 0 additional units.
 * @param state
 * @param skuId The skuId for the productVersion
 * @param additionalUnitQuantity The quantity of additional units to add
 */

export const getProductVersionConfigsWithUnits = (state, {
  skuId,
  additionalUnitQuantity = 0
}) => {
  const isUngatedForSeats = getIsUngatedForSeats(state);
  const ownsProductVersion = getHasProductVersionInProductInventory(state, {
    skuId
  });
  const product = getProductForSkuId(state, {
    skuId
  });
  const productVersion = getCurrentProductVersion(state, {
    productId: product.id
  });
  const isQuantifiable = productVersion && productVersion.quantifiable;
  const productConfigurations = [];
  if (!ownsProductVersion || isUngatedForSeats && ownsProductVersion && isQuantifiable) productConfigurations.push(createProductConfig({
    skuId
  }));
  const quantityPack = getQuantityPackForProductVersion(state, {
    productVersionSkuId: skuId
  });

  if (quantityPack && (additionalUnitQuantity || ownsProductVersion)) {
    productConfigurations.push(createProductConfig({
      skuId: quantityPack.skuId,
      quantity: additionalUnitQuantity
    }));
  } // For seats, the interstitial is expecting productConfigurations to not be empty.
  // productConfigurations will be empty for any owned products that have no quantity packs
  // and aren't quantifiable hubs.
  // To workaround this, we're adding the core seat to productConfigurations
  // which would be the only logical thing to buy on the interstitial in that situation anyway


  if (isUngatedForSeats && ownsProductVersion && !isQuantifiable && !quantityPack) {
    const {
      skuId: coreSeatSkuId
    } = getCoreSeatSkuForProductLevel(state, {
      productLevel: product.productLevel
    });
    productConfigurations.push(createProductConfig({
      skuId: coreSeatSkuId,
      quantity: 0
    }));
  }

  return productConfigurations;
};
export const getHighestProductLevelInProductConfigurations = (state, {
  productConfigurations
}) => {
  const productConfigurationsHubSkuIds = productConfigurations.map(({
    skuId
  }) => skuId).filter(skuId => getSkuIdIsHub(state, {
    skuId
  }));
  return getHighestProductLevelFromSkuIds(state, {
    skuIds: productConfigurationsHubSkuIds
  });
};
export const getProductsAndPacksForQueryParams = (state, {
  productConfigurations
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;

  if (!productConfigurations.length) {
    return {
      products: [],
      packs: []
    };
  }

  const tempProducts = [];
  const tempPacks = [];
  productConfigurations.forEach(({
    skuId,
    quantity
  }) => {
    const sku = skuMap[skuId];

    if (sku.skuType === QUANTITY_PACK) {
      tempPacks.push(`${sku.parcelName}_${quantity}`);
    } else {
      const {
        apiName
      } = getProductForSkuId(state, {
        skuId
      });
      tempProducts.push(`${apiName}_${quantity}`);
    }
  });
  return {
    products: tempProducts,
    packs: tempPacks
  };
};
export const getInterstitialUrl = (state, {
  productConfigurations
}) => {
  const queryParams = getProductsAndPacksForQueryParams(state, {
    productConfigurations
  });
  const queryString = stringifyQueryParams(queryParams);
  return `${getFullUrl('app')}/pricing-beta/${PortalIdParser.get()}/interstitial?${queryString}`;
};
export const getProductLevelForCoreSeats = (state, {
  productConfigurations
}) => {
  const productInventorySkuIds = getProductVersionsFromProductInventory(state).map(({
    skuId
  }) => skuId);
  const productConfigurationsSkuIds = productConfigurations.map(({
    skuId
  }) => skuId);
  const hubSkuIds = [...new Set([...productInventorySkuIds, ...productConfigurationsSkuIds])].filter(skuId => getSkuIdIsHub(state, {
    skuId
  }));
  const highestProductLevel = getHighestProductLevelFromSkuIds(state, {
    skuIds: hubSkuIds
  });
  const productLevelToUse = currentOrderedPaidProductLevels.includes(highestProductLevel) ? highestProductLevel : STARTER;
  return productLevelToUse;
};
export const getIncludedCoreSeatsQuantity = (state, {
  productConfigurations
}) => {
  const hubSkuIds = productConfigurations.filter(prodConfig => {
    const {
      skuId
    } = prodConfig;
    return getSkuIdIsHub(state, {
      skuId
    });
  }).map(({
    skuId
  }) => skuId);
  const ownsIncludedStarterCoreSeat = getHasProductInProductInventory(state, {
    productAttributeFilters: {
      apiName: CORE_SEATS_INCLUDED_STARTER
    }
  });
  const isStarterCoreSeatRequiredForSkuIds = getIsStarterCoreSeatRequired(state, {
    productConfigurations
  });
  let includedCoreSeatsQuantity = 0;

  if (isStarterCoreSeatRequiredForSkuIds && !ownsIncludedStarterCoreSeat) {
    includedCoreSeatsQuantity = includedCoreSeatsQuantity + 1;
  }

  return hubSkuIds.reduce((total, hubSkuId) => {
    const includedCoreSeatsQuantityForSkuId = getIncludedCoreSeatsQuantityForProductVersion(state, {
      productVersionSkuId: hubSkuId
    });
    return total + includedCoreSeatsQuantityForSkuId;
  }, includedCoreSeatsQuantity);
};
/**
 * The purpose of this util is to return productConfigurations while considering
 * whether the configuration is currently, or no longer, eligible for the free
 * starter core seat.
 *
 * A portal should be eligible for the free starter core seat upon their first
 * purchase of a starter product. On subsequent starter purchases, they are
 * no longer eligible.
 *
 * A portal would also be no longer eligible if, for example on the Create a
 * Bundle page, they initially started off with viewing a starter product
 * configuration, but then selected all pro products instead.
 */

export const getProductConfigurationWithFreeCoreSeatIfPossible = (state, {
  productConfigurations
}) => {
  const skuIds = productConfigurations.map(config => config.skuId);
  const ownsIncludedStarterCoreSeat = getHasProductInProductInventory(state, {
    productAttributeFilters: {
      apiName: CORE_SEATS_INCLUDED_STARTER
    }
  });
  const isStarterCoreSeatRequired = getIsStarterCoreSeatRequiredForSkuIds(state, {
    skuIds
  });
  const coreSeatsIncludedStarterSkuId = getCurrentSkuIdForProduct(state, {
    apiName: CORE_SEATS_INCLUDED_STARTER
  });
  const foundConfig = productConfigurations.find(({
    skuId
  }) => skuId === coreSeatsIncludedStarterSkuId);
  const includedStarterCoreSeatQuantity = foundConfig ? foundConfig.quantity : 0; // remove if productConfigs have no starter

  if (!isStarterCoreSeatRequired && foundConfig) {
    return productConfigurations.filter(config => config.skuId !== coreSeatsIncludedStarterSkuId);
  } // checking for quantity > 1 because there should not be more than
  // 1 starter core seat included, which can be altered through
  // the query parameters


  if ((includedStarterCoreSeatQuantity === 0 || includedStarterCoreSeatQuantity > 1) && !ownsIncludedStarterCoreSeat && isStarterCoreSeatRequired) {
    const filteredConfigs = productConfigurations.filter(({
      skuId
    }) => skuId !== coreSeatsIncludedStarterSkuId);
    filteredConfigs.push(createProductConfig({
      skuId: coreSeatsIncludedStarterSkuId,
      quantity: 1
    }));
    return filteredConfigs;
  }

  return productConfigurations;
};